// Shared patent zone constants and interfaces for compound intelligence
import { type ColourPalette, getColourPalette, getSavedVisionType } from './colourPalettes';

export interface ZoneCount {
  zone: number;
  count: number;
  label: string;
}

export interface PatentMatch {
  patent_id: string;
  patent_number: string;
  similarity: number;
  assignee: string;
  title: string;
  claim_smiles?: string;
}

export interface ProteinTarget {
  target_name: string;
  uniprot_id?: string;
  chembl_id?: string;
  activity_type?: string;
  activity_value?: number;
  activity_units?: string;
  assay_type?: string;
  source?: string;
}

export interface CompoundPathway {
  pathway_id: string;
  pathway_name: string;
  source: string;
  source_type: 'direct' | 'target';
  species?: string;
  evidence_code?: string;
}

export interface ORDReaction {
  reaction_id: string;
  reaction_smiles: string | null;
  dataset_name: string | null;
  role: 'reactant' | 'product' | 'both';
}

export interface CompoundAnalysis {
  smiles: string;
  zone_counts: ZoneCount[];
  patents: PatentMatch[];
  properties: Record<string, any>;
  pubchem_cid?: number;
  targets: ProteinTarget[];
  pathways: CompoundPathway[];
}

// Get current zone colours based on saved vision type preference
const getCurrentPalette = (): ColourPalette => getColourPalette(getSavedVisionType());

// Dynamic ZONE_COLORS — reads from saved preference at call time
// For components using React context, prefer useColourPalette().palette.zones instead
export const ZONE_COLORS: Record<number, string> = new Proxy(
  { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#3b82f6', 5: '#22c55e' } as Record<number, string>,
  {
    get(_target, prop) {
      if (typeof prop === 'string' && !isNaN(Number(prop))) {
        return getCurrentPalette().zones[Number(prop)] || '#808080';
      }
      return Reflect.get(_target, prop);
    }
  }
);

export const ZONE_LABELS: Record<number, string> = {
  1: 'Literal Match',
  2: 'High Similarity',
  3: 'Moderate',
  4: 'Low Similarity',
  5: 'Novel',
};

export const ZONE_DESCRIPTIONS: Record<number, string> = {
  1: 'Identical structure \u2014 salts stripped, charges standardised, business rules applied. Stereochemically identical (E/Z, R/S)',
  2: 'Constitutionally identical \u2014 same atoms, same bond connectivity, but stereochemically different isomers',
  3: 'Graph isomorphic \u2014 canonical SMILES match (RDKit)',
  4: 'Fingerprint match above threshold (Tanimoto \u2265 0.85)',
  5: 'No significant structural overlap with patent compounds',
};

export const ZONE_THRESHOLDS: Record<number, { min: number; max: number }> = {
  1: { min: 0.95, max: 1.0 },
  2: { min: 0.85, max: 0.95 },
  3: { min: 0.75, max: 0.85 },
  4: { min: 0.60, max: 0.75 },
  5: { min: 0, max: 0.60 },
};

export const getZoneFromSimilarity = (similarity: number): number => {
  if (similarity >= 0.95) return 1;
  if (similarity >= 0.85) return 2;
  if (similarity >= 0.75) return 3;
  if (similarity >= 0.60) return 4;
  return 5;
};
